import React, { useState } from 'react';
import { trackEvent, BOOKING_SCREENING_URL } from '../lib/tracking';
import { optimizeImage } from '../lib/image';
import VideoTestimonialModal from './VideoTestimonialModal';

export default function TestimonialsSection({ showVideo = true, title = 'Succeshistorier & Klientoplevelser', subtitle = 'Reelle oplevelser fra personer, der har skabt forandring' }) {
  const [activeVideo, setActiveVideo] = useState(null);

  const testimonials = [
    {
      name: 'Birgitte',
      age: 43,
      problem: 'Selvværd & ro i hverdagen',
      quote: 'Kim har hjulpet mig med at genfinde min selvtillid og skabe ro i mit liv. Gennem hans varme og nærværende tilgang følte jeg mig tryg og forstået fra første session. Jeg har nu redskaberne til at håndtere udfordringer og se nye muligheder.',
      before: 'Lav selvtillid, manglende ro og selvkritik',
      after: 'Genfundet selvtillid og konkrete værktøjer til hverdagen',
    },
    {
      name: 'Søren',
      age: 52,
      problem: 'Krise, sorg & fastlåsthed',
      quote: 'Jeg fik hjælp til at finde klarhed i en svær tid, hvor jeg følte mig fastlåst. Kims indsigt og guidning har ikke kun løst de problemer, jeg kom med, men også givet mig værktøjer til at skabe forandringer i mit liv. Jeg kan varmt anbefale ham.',
      before: 'Følte sig fastlåst i en svær livssituation',
      after: 'Fuld klarhed, retning og løste de akutte udfordringer',
    },
    {
      name: 'Josefine',
      age: 26,
      problem: 'Angst & negative mønstre',
      quote: 'Efter et forløb hos Kim har jeg lært at tackle min angst og ændre negative mønstre. Hans evne til at stille de rigtige spørgsmål og støtte mig i processen har gjort en stor forskel. Jeg føler mig stærkere og mere i balance.',
      before: 'Angst og hæmmende vanemønstre',
      after: 'Lært at tackle angsten, føler sig stærkere og i balance',
    },
  ];

  return (
    <section className="py-20 sm:py-24 bg-[#ECEEF1] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-14 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#5A232E]/10 text-[#5A232E] text-xs font-semibold uppercase tracking-wider mb-4">
            <i className="fa-solid fa-heart-pulse text-[#5A232E]" aria-hidden="true" />
            Social Proof & Tryghed
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#5A232E] tracking-tight">
            {title}
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#6c7480] leading-relaxed">
            {subtitle}
          </p>
        </div>

        {/* Testimonials 3-column responsive grid (3 desktop, 2 tablet, 1 mobile) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-16">
          {testimonials.map((item, idx) => (
            <div
              key={idx}
              className="bg-white rounded-2xl p-7 sm:p-8 border border-[#D9DDE2] shadow-xs hover:shadow-lg transition-all duration-300 flex flex-col justify-between"
            >
              <div>
                {/* Rating stars & verified badge */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex text-amber-500 gap-1 text-sm">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="fa-solid fa-star" aria-hidden="true" />
                    ))}
                  </div>
                  <span className="text-[11px] font-semibold text-[#5A232E] bg-[#ECEEF1] px-2.5 py-1 rounded-full border border-[#D9DDE2]">
                    Verificeret klient
                  </span>
                </div>

                <div className="text-xs font-semibold text-[#5A232E] uppercase tracking-wider mb-3">
                  {item.problem}
                </div>

                <blockquote className="text-sm sm:text-base text-[#11161d] leading-relaxed italic mb-6">
                  "{item.quote}"
                </blockquote>
              </div>

              <div className="pt-5 border-t border-[#D9DDE2]/80">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-display font-semibold text-base text-[#5A232E]">
                      {item.name}
                    </div>
                    <div className="text-xs text-[#6c7480]">
                      {item.age} år · Individuelt forløb
                    </div>
                  </div>
                  <div className="w-9 h-9 rounded-full bg-[#5A232E]/10 text-[#5A232E] flex items-center justify-center font-display font-semibold text-sm">
                    {item.name[0]}
                  </div>
                </div>

                {/* Before / After micro indicators */}
                <div className="mt-4 pt-3 border-t border-[#ECEEF1] space-y-1.5 text-xs">
                  <div className="flex items-start gap-2 text-[#6c7480]">
                    <span className="font-semibold text-red-700/80 shrink-0">Før:</span>
                    <span className="leading-relaxed">{item.before}</span>
                  </div>
                  <div className="flex items-start gap-2 text-[#5A232E]">
                    <span className="font-semibold text-emerald-700 shrink-0">Efter:</span>
                    <span className="leading-relaxed">{item.after}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Video Testimonial Section - Clean authentic representation without fabricated stories */}
        {showVideo && (
          <div className="bg-[#5A232E] rounded-3xl p-8 sm:p-12 text-[#ECEEF1] border border-white/10 shadow-xl mb-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7 space-y-4">
                <span className="text-xs font-semibold uppercase tracking-wider text-emerald-300 flex items-center gap-1.5">
                  <i className="fa-solid fa-video" aria-hidden="true" />
                  Mød din terapeut
                </span>
                <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl text-white">
                  Se hvordan en samtale i praksis foregår
                </h3>
                <p className="text-sm sm:text-base text-[#ECEEF1]/80 leading-relaxed">
                  Hos Kim er der højt til loftet og plads til at være sårbar. Hør Kim fortælle om sin tilgang til nervesystemet, trygge rammer og vejen til varig forandring.
                </p>
                <div className="flex flex-wrap items-center gap-4 pt-2">
                  <button
                    type="button"
                    onClick={() =>
                      setActiveVideo({
                        src: 'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/mvbluz5gen2ai-6usxnt-pgxl3eij6r5-strbjswx5cyjgcioprua1st5mycp4ounbnqouuhtmzm.mp4',
                        poster: 'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/img-8587.jpeg',
                        title: 'Mød Kim Palasz i praksis på Amager',
                      })
                    }
                    className="inline-flex items-center gap-2.5 px-6 py-3 rounded-xl bg-[#5A232E] hover:bg-[#431722] text-white font-semibold text-sm shadow-md transition-all cursor-pointer"
                  >
                    <i className="fa-solid fa-play text-xs" aria-hidden="true" />
                    <span>Se videointroduktion</span>
                  </button>
                  <span className="text-xs text-[#ECEEF1]/60">Varighed: 1½ min.</span>
                </div>
              </div>

              {/* Video Thumbnail Facade */}
              <div className="lg:col-span-5">
                <div
                  onClick={() =>
                    setActiveVideo({
                      src: 'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/mvbluz5gen2ai-6usxnt-pgxl3eij6r5-strbjswx5cyjgcioprua1st5mycp4ounbnqouuhtmzm.mp4',
                      poster: 'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/img-8587.jpeg',
                      title: 'Mød Kim Palasz i praksis på Amager',
                    })
                  }
                  className="group relative aspect-video sm:aspect-4/3 rounded-2xl overflow-hidden shadow-2xl border border-white/20 cursor-pointer bg-black/40"
                >
                  <img
                    src={optimizeImage(
                      'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/img-8587.jpeg',
                      800
                    )}
                    alt="Kim Palasz i samtalerummet på Amager"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-90"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-[#5A232E] text-white flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                      <i className="fa-solid fa-play text-xl ml-1" aria-hidden="true" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bottom CTA bar */}
        <div className="text-center pt-4">
          <a
            href={BOOKING_SCREENING_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent('cta_booking_click', { location: 'testimonials_bottom' })}
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[#5A232E] hover:bg-[#431722] text-white font-semibold text-sm shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5 no-break whitespace-nowrap"
          >
            <i className="fa-solid fa-calendar-check" aria-hidden="true" />
            <span>Book en gratis afklaringssamtale</span>
          </a>
        </div>
      </div>

      {activeVideo && (
        <VideoTestimonialModal
          isOpen={Boolean(activeVideo)}
          onClose={() => setActiveVideo(null)}
          videoSrc={activeVideo.src}
          posterSrc={activeVideo.poster}
          title={activeVideo.title}
        />
      )}
    </section>
  );
}
